export const careGapDocumentationMarkdown = `
# Care Gap Algorithm

## Description

The Care Gap Algorithm block identifies missing recommended preventative care services
for patients based on Healthcare Effectiveness Data and Information Set (HEDIS) guidelines.
It analyzes patient data to detect potential care gaps and generate findings.

## Input Parameters

This block requires the following input parameters:

- **Algorithm**: The specific care gap algorithm to be used. Options include:
  - Blood Pressure Control for Diabetes
  - Cervical Cancer Screening
  - Colon Cancer Screening (High Risk)
  - Diabetes Eye Exam
  - Diabetes HbA1c Monitoring
  - Diabetes Nephropathy Monitoring
  - Hypertension
  - Kidney Health Evaluation for Patients with Diabetes
  - Osteoporosis Screening for Older Women
  - Breast Cancer Screening (Women 50-75 years)
  - Colon Cancer Screening
  - Preventive Care Pap Smear
  - Statin Therapy for Cardiovascular Disease
  - Statin Therapy for Diabetes

- **Payload**: The payload required to run the care gap algorithm.
Here is the required information and the corresponding source blocks to fetch that information:

  - **Documents**: Patient's documents extracted from the Document Extract block
  - **Problems**: Patient's conditions extracted from the FHIR service's Conditions block configured to fetch problems
  - **Medications**: Patient's medications extracted from the FHIR service's Medications block configured to fetch medications
  - **Lab Values**: Patient's lab results extracted from the FHIR service's LabValues block configured to fetch lab values
  - **Procedures**: Patient's surgical history extracted from the FHIR service's SurgicalHistories block configured to fetch procedures

## Output

The block outputs findings that identify potential care gaps for the patient. Each finding includes:

- Type of care gap
- EHR Information ID
- Encounter ID (if applicable)
- Patient Advisory ID (if applicable)
- Care gap type
- Status
- Review state
- Review date (if reviewed)
- Rejection reason (if rejected)
- Export state
- Evidence (supporting data for the finding)

Evidence for each finding may include:
- Provider name and specialty
- Location
- Document date
- Impression and recommendation
- Recall duration
- Source
- Test name, date, value, and result

## Process

1. The block collects required patient details from the specified source blocks.
2. It sends a request to the predict service with the collected data.
3. The predict service evaluates the data using the specified algorithm and identifies care gaps.
4. The findings are returned as the block's output.

## Usage

This block will be typically used in care gap outreach flows to:
- Identify specific preventive care needs for patients
- Notify patients about missing care via text or email
- Gather relevant health information
- Schedule appointments to address care gaps
- Update patient charts with collected information
`;
